import { useEffect, useRef, useState } from 'react';
import { audio } from '@tt/audio';

interface Props { onSuccess: () => void }

/**
 * Enter-the-Tavern entry screen — now a full 3D treatment.
 *
 * The screen itself is deliberately Three-FREE and paints instantly: it is
 * just a warm tavern-chrome "threshold" panel (wordmark + tagline + the
 * hot-brass "Enter the Tavern" key) floating over a transparent stage. That
 * means the CTA is interactive at first paint with zero dependency on WebGL.
 *
 * Behind it, App.tsx mounts the SAME persistent 3D tavern hall used by the
 * menus ({@link MenuBackdrop3D} → {@link RoomBackdrop3DScene} `menu` variant)
 * on the `z-index:-1` fixed layer. That backdrop is lazy (its own Three chunk),
 * so it streams in AFTER this form is already usable, and on WebGL/chunk
 * failure the shared 2D {@link Background} tavern SVG (mode `gate`) shows
 * through underneath — the entry screen is never blank and never blocks on 3D.
 *
 * The passcode is gone — the door is always unlocked. The single CTA POSTs to
 * /api/auth/gate to mint the `tt_gate` session cookie, plays the amber
 * threshold flash, then transitions into the app.
 */
export function Gate({ onSuccess }: Props) {
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutsRef = useRef<number[]>([]);

  // Cancel pending success-callback timers if the component unmounts mid-anim.
  useEffect(() => () => {
    for (const id of timeoutsRef.current) window.clearTimeout(id);
    timeoutsRef.current = [];
  }, []);

  async function open() {
    if (busy || opening) return;
    setError(null);
    try {
      audio.unlock();
      audio.setMood('gate');
      const sfx = audio.sfx as Partial<{ doorOpen: () => void; bell: () => void }>;
      if (typeof sfx.doorOpen === 'function') sfx.doorOpen();
      else if (typeof sfx.bell === 'function') sfx.bell();
    } catch { /* audio is best-effort */ }
    setBusy(true);
    setOpening(true);
    try {
      const res = await fetch('/api/auth/gate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enter: true }),
      });
      if (!res.ok) {
        setError(`Couldn't open the door (${res.status}). Tap to try again.`);
        setBusy(false);
        setOpening(false);
        return;
      }
    } catch {
      setError('Network error. Tap to try again.');
      setBusy(false);
      setOpening(false);
      return;
    }
    // Schedule the transition: amber flash peaks at ~600ms, onSuccess at ~1000ms.
    timeoutsRef.current.push(window.setTimeout(() => { onSuccess(); }, 1000));
  }

  return (
    <main
      className="full-center gate-screen gate-screen-3d"
      onClick={open}
    >
      {/* The tavern-chrome threshold panel. Everything the user reads lives on
          this warm wood/brass plaque so it stays legible over the busy live
          3D hall behind it. The whole screen is a click target (the panel
          just stops propagation so its inner button owns the tap). */}
      <div
        className="gate-threshold"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="gate-eyebrow display" aria-hidden="true">tonight at the table…</p>
        <h1 className="gate-wordmark display">Tile&nbsp;Tavern</h1>

        <button
          type="button"
          className="btn primary big gate-enter-cta"
          onClick={(e) => { e.stopPropagation(); void open(); }}
          disabled={busy || opening}
        >
          {opening ? 'Opening…' : 'Enter the Tavern'}
        </button>

        <p className="gate-plaque muted">No accounts. Just a name, a face, and a 4-digit PIN.</p>

        {error && <div role="alert" className="gate-rejection">{error}</div>}
      </div>

      {/* Amber overlay flash during the open transition — sits above everything
          but is non-interactive. */}
      <div className={`gate-overlay${opening ? ' is-flashing' : ''}`} aria-hidden="true" />
    </main>
  );
}
